import type { ReactNode } from "react";
import type { BannerTemplate } from "src/features/HomepageCarouselBanners/models";

import { useController } from "react-hook-form";
import { Image, Video, Type } from "lucide-react";

export interface TemplateOptionProps {
	value: string;
	currentValue: string;
	onChange: (val: string) => void;
	icon: ReactNode;
	label: string;
}

const TemplateOption = ({
	value,
	currentValue,
	onChange,
	icon,
	label
}: TemplateOptionProps) => {
	const isChecked = currentValue === value;

	return (
		<label className="flex-1 cursor-pointer">
			<input
				type="radio"
				className="sr-only"
				value={value}
				checked={isChecked}
				onChange={() => onChange(value)}
			/>

			<div
				className={[
					"flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200",
					isChecked
						? "bg-primary text-white shadow-md shadow-primary/50"
						: "text-muted-foreground hover:text-foreground hover:bg-white/80"
				].join(" ")}
			>
				{icon}
				<span>{label}</span>
			</div>
		</label>
	);
};

export interface BannerTemplatePickerProps {
	name: string;
	defaultValue?: BannerTemplate;
}

const BannerTemplatePicker = ({ name }: BannerTemplatePickerProps) => {
	const { field } = useController({ name });

	return (
		<div className="mb-6">
			<fieldset>
				<legend className="sr-only">Banner template</legend>

				<div className="inline-flex w-full gap-1 p-1.5 bg-white border border-border rounded-xl shadow-xs">
					<TemplateOption
						value="fullImageBanner"
						currentValue={field.value}
						onChange={field.onChange}
						icon={<Image className="h-4 w-4" />}
						label="Image"
					/>

					<TemplateOption
						value="videoBanner"
						currentValue={field.value}
						onChange={field.onChange}
						icon={<Video className="h-4 w-4" />}
						label="Video"
					/>

					<TemplateOption
						value="textBanner"
						currentValue={field.value}
						onChange={field.onChange}
						icon={<Type className="h-4 w-4" />}
						label="Text"
					/>
				</div>
			</fieldset>
		</div>
	);
};

export default BannerTemplatePicker;
